import { useState, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Container,
  InputGroup,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetJobByIdQuery,
  useUpdateJobMutation,
  useGetJobCategoriesQuery,
} from "../../../redux/api/CareersApi/careersApi";
import { FaArrowLeft, FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { message } from "antd";
import LoaderSpinner from "../../../Components/LoaderSpinner";

const EditJob = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: jobData, isLoading: isFetching } = useGetJobByIdQuery(id);
  const [updateJob, { isLoading: isUpdating }] = useUpdateJobMutation();

  const { data: jobCategories, isLoading: isCatsLoading } =
    useGetJobCategoriesQuery();

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category_id: "",
    location: "Chennai, India",
    jobType: "Full-Time",
    experienceLevel: "Experienced",
    roleDescription: "",
    responsibilities: [""],
    skills: [""],
    requirements: [""],
    benefits: [""],
    display_order: 0,
    status: true,
    meta_title: "",
    meta_keyword: "",
    meta_description: "",
  });

  useEffect(() => {
    if (jobData) {
      setFormData({
        ...jobData,
        responsibilities: jobData.responsibilities?.length
          ? jobData.responsibilities
          : [""],
        skills: jobData.skills?.length ? jobData.skills : [""],
        requirements: jobData.requirements?.length
          ? jobData.requirements
          : [""],
        benefits: jobData.benefits?.length ? jobData.benefits : [""],
      });
    }
  }, [jobData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData((prev) => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateJob({ id, ...formData }).unwrap();
      message.success("Job updated successfully!");
      navigate("/admin/jobs");
    } catch (error) {
      message.error(error.data?.error || "Failed to update job");
    }
  };

  if (isFetching || isCatsLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button
          variant="light"
          onClick={() => navigate("/admin/jobs")}
          className="rounded-circle shadow-sm p-2"
        >
          <FaArrowLeft />
        </Button>
        <h2 className="fw-bold text-dark mb-0">Edit Job Opening</h2>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
              <h5 className="fw-bold mb-4">Basic Information</h5>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Job Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g. Senior Software Developer"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Slug (URL)</Form.Label>
                    <Form.Control
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Category</Form.Label>
                    <Form.Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {jobCategories
                        ?.filter(
                          (c) => c.status || c.id === formData.category_id,
                        )
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="e.g. Chennai, India"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Job Type</Form.Label>
                    <Form.Control
                      type="text"
                      name="jobType"
                      value={formData.jobType}
                      onChange={handleChange}
                      placeholder="e.g. Full-Time"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Experience Level
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="experienceLevel"
                      value={formData.experienceLevel}
                      onChange={handleChange}
                      placeholder="e.g. Experienced"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Role Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="roleDescription"
                      value={formData.roleDescription}
                      onChange={handleChange}
                      placeholder="Enter a brief overview of the role..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card>

            {/* Dynamic Lists Section */}
            {[
              { id: "responsibilities", label: "Responsibilities" },
              { id: "skills", label: "Skills You Need" },
              { id: "requirements", label: "Great Fit If You" },
              { id: "benefits", label: "Benefits / Why Join Us" },
            ].map((section) => (
              <Card
                key={section.id}
                className="border-0 shadow-sm rounded-4 p-4 mb-4"
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">{section.label}</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="rounded-circle p-2"
                    onClick={() => addArrayItem(section.id)}
                  >
                    <FaPlus />
                  </Button>
                </div>
                {formData[section.id].map((item, index) => (
                  <InputGroup key={index} className="mb-3">
                    <Form.Control
                      value={item}
                      onChange={(e) =>
                        handleArrayChange(section.id, index, e.target.value)
                      }
                      placeholder={`Enter ${section.label.toLowerCase()}...`}
                    />
                    {formData[section.id].length > 1 && (
                      <Button
                        variant="outline-danger"
                        onClick={() => removeArrayItem(section.id, index)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </InputGroup>
                ))}
              </Card>
            ))}

            <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
              <h5 className="fw-bold mb-4">SEO Details (Optional)</h5>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Meta Title</Form.Label>
                <Form.Control
                  type="text"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  placeholder="Enter SEO meta title..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Meta Keywords</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="meta_keyword"
                  value={formData.meta_keyword}
                  onChange={handleChange}
                  placeholder="keyword1, keyword2, keyword3..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Meta Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  placeholder="Enter SEO meta description..."
                />
              </Form.Group>
            </Card>
          </Col>

          <Col lg={4}>
            <Card
              className="border-0 shadow-sm rounded-4 p-4 mb-4 sticky-top"
              style={{ top: "20px" }}
            >
              <h5 className="fw-bold mb-4">Update Statistics</h5>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Display Order</Form.Label>
                <Form.Control
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Check
                  type="switch"
                  id="status-switch"
                  label="Active and Visible"
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                  className="fw-bold"
                />
              </Form.Group>
              <hr />
              <div className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="rounded-pill py-2 shadow-sm"
                  disabled={isUpdating}
                >
                  <FaSave className="me-2" />
                  {isUpdating ? "Updating..." : "Update Job Opening"}
                </Button>
                <Button
                  variant="light"
                  className="rounded-pill py-2"
                  onClick={() => navigate("/admin/jobs")}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default EditJob;
